import { newId, nowIso } from "./ids.js";
import type { CenterlineDocument, PageInfo, PageScale, SheetLabel } from "./types.js";
import { defaultLayers, FORMAT_ID, FORMAT_VERSION } from "./types.js";
import { mapDesktopRecords } from "./markup/desktop.js";

export function emptyDocument(init: { name: string; blobHash: string; byteLength: number; fileName: string; id?: string; now?: string }): CenterlineDocument {
  const now = init.now ?? nowIso();
  return {
    format: FORMAT_ID,
    formatVersion: FORMAT_VERSION,
    id: init.id ?? newId(),
    name: init.name,
    createdAt: now,
    updatedAt: now,
    plan: { blobHash: init.blobHash, byteLength: init.byteLength, fileName: init.fileName, pageCount: 0, pages: [] },
    pageScales: {},
    sheetLabels: {},
    layers: defaultLayers(),
    measurements: [],
    markups: [],
    stampLibrary: [],
  };
}

/**
 * Brings a stored document up to the current revision of format v1:
 * Phase 1 documents have no markup arrays (their desktop records sit in
 * `desktop`), and kept the clean plan of a Centerline PDF in `sourceBlobHash`.
 * Pure: returns a new object when anything changed.
 */
export function upgradeDocument(raw: CenterlineDocument): CenterlineDocument {
  const d = raw as Partial<CenterlineDocument> & CenterlineDocument;
  if (Array.isArray(d.markups) && Array.isArray(d.measurements) && Array.isArray(d.stampLibrary)) return raw;
  const doc: CenterlineDocument = { ...d, measurements: d.measurements ?? [], markups: d.markups ?? [], stampLibrary: d.stampLibrary ?? [] };
  if (d.plan.sourceBlobHash && d.import?.kind === "centerline-pdf") {
    const { sourceBlobHash, ...plan } = d.plan;
    doc.plan = { ...plan, blobHash: sourceBlobHash, flattenedBlobHash: d.plan.blobHash };
  }
  if (!Array.isArray(d.markups) && d.desktop) {
    const mapped = mapDesktopRecords(d.desktop, doc.plan.pages, doc.updatedAt);
    doc.measurements = mapped.measurements;
    doc.markups = mapped.markups;
    if (mapped.leftover.markups.length || mapped.leftover.measurements.length) doc.desktop = mapped.leftover;
    else delete doc.desktop;
  }
  return doc;
}

export function pageAt(doc: CenterlineDocument, index: number): PageInfo | undefined {
  return doc.plan.pages[index];
}

export function scaleForIndex(doc: CenterlineDocument, index: number): PageScale | undefined {
  const page = doc.plan.pages[index];
  return page ? doc.pageScales[page.pageId] : undefined;
}

/** `sheetLabel(for:)`: label display, else "Sheet N". */
export function sheetLabelText(doc: CenterlineDocument, index: number): string {
  const page = doc.plan.pages[index];
  const label = page ? doc.sheetLabels[page.pageId] : undefined;
  return label ? sheetLabelDisplay(label) : `Sheet ${index + 1}`;
}

/** `sheetNumber(for:)`: short form for thumbnails. */
export function sheetNumberText(doc: CenterlineDocument, index: number): string {
  const page = doc.plan.pages[index];
  return (page && doc.sheetLabels[page.pageId]?.number) || `${index + 1}`;
}

/** `SheetLabel.display`: "C-101 — Title" or just the number. */
export function sheetLabelDisplay(label: SheetLabel): string {
  return label.title ? `${label.number} — ${label.title}` : label.number;
}

/** Display size of a page in points after /Rotate (what a viewer shows). */
export function displaySize(page: PageInfo): { width: number; height: number } {
  const [, , w, h] = page.cropBox;
  return page.rotate % 180 === 0 ? { width: w, height: h } : { width: h, height: w };
}

/** "24 × 36 in" style paper size from the media box. */
export function paperSizeText(page: PageInfo): string {
  const w = page.mediaBox[2] / 72, h = page.mediaBox[3] / 72;
  const fmt = (v: number) => (Math.abs(v - Math.round(v)) < 0.01 ? `${Math.round(v)}` : v.toFixed(1));
  const s = displaySize(page);
  return s.width >= s.height ? `${fmt(Math.max(w, h))} × ${fmt(Math.min(w, h))} in` : `${fmt(Math.min(w, h))} × ${fmt(Math.max(w, h))} in`;
}
